/**
 * The one Panel money formatter. Every restaurant amount is integer euro cents
 * (docs/decisions/2026-09-28-spain-only-eur-and-madrid.md): this module
 * formats them exactly, with BigInt scaling and no float step, and gives the
 * text an amount field starts from. Typed euro text is read by the Panel's one
 * figure reader (`spanish-number.ts`: `parseEuroCents`, `parseBasisPoints`), so
 * a boot module that only shows an amount never carries the reader. Screens
 * wrap these only to say their own missing-value word.
 */

/** The one currency label a wire field still carries. */
export const EUR = 'EUR' as const;

const SCALE = 100n;

export interface EuroFormatOptions {
  /**
   * `always` (the default): grouped from 1.000 («1.234,50 €»), so every amount on one screen reads alike;
   * `locale`: the es-ES `Intl` rule, none below 10.000 («1234,50 €», «12.345,50 €»); `none`: never (input text).
   */
  readonly grouping?: 'locale' | 'always' | 'none';
  /** `always`: «12,00 €»; `nonzero`: «12 €» but «12,50 €»; `none`: whole euros, half away from zero. */
  readonly cents?: 'always' | 'nonzero' | 'none';
  /** A leading plus for positive amounts; negative amounts always carry U+2212. */
  readonly signed?: boolean;
}

function group(digits: string, grouping: NonNullable<EuroFormatOptions['grouping']>): string {
  const minimum = grouping === 'none' ? Infinity : grouping === 'always' ? 4 : 5;
  return digits.length < minimum ? digits : digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function hundredthsText(value: number, options: EuroFormatOptions): string | null {
  if (!Number.isSafeInteger(value)) return null;
  const { grouping = 'always', cents = 'always' } = options;
  const absolute = BigInt(Math.abs(value));
  let whole = absolute / SCALE;
  const fraction = absolute % SCALE;
  if (cents === 'none' && fraction * 2n >= SCALE) whole += 1n;
  const decimal = cents === 'none' || (cents === 'nonzero' && fraction === 0n) ? '' : `,${String(fraction).padStart(2, '0')}`;
  const shown = whole !== 0n || decimal.replace(/[,0]/g, '') !== '';
  const sign = value < 0 && shown ? '−' : options.signed && value > 0 ? '+' : '';
  return `${sign}${group(String(whole), grouping)}${decimal}`;
}

/** Formats integer euro cents («24,50 €», a no-break space before the symbol), or null when the amount is not a safe integer. */
export function formatEuroCents(cents: number, options: EuroFormatOptions = {}): string | null {
  const text = hundredthsText(cents, options);
  return text === null ? null : `${text}\u00a0€`;
}

// Three significant digits, no forced decimals: the tick states the gridline's
// exact value («1,25 mil €») and prints the same under every ICU build («300 €»).
const COMPACT = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', notation: 'compact', minimumFractionDigits: 0, maximumSignificantDigits: 3 });

/** Axis-tick money («1,5 mil €»): presentation only, never a figure a screen states. */
export function formatEuroCentsCompact(cents: number): string | null {
  if (!Number.isSafeInteger(cents)) return null;
  return COMPACT.format(cents / 100).replace('-', '−');
}

/** The text an amount field starts from: no symbol, no grouping, «12» or «12,50» («12,00» with `always`). */
export function euroCentsInputText(cents: number, options: Pick<EuroFormatOptions, 'cents' | 'signed'> = {}): string | null {
  return hundredthsText(cents, { grouping: 'none', cents: options.cents ?? 'nonzero', signed: options.signed ?? false })?.replace('−', '-') ?? null;
}

/** Basis points as percentage input text: 1250 → «12,50», 1200 → «12». */
export function basisPointsInputText(basisPoints: number): string | null {
  return hundredthsText(basisPoints, { grouping: 'none', cents: 'nonzero' });
}
